import { ChartData, Chart as ChartJS, ChartTypeRegistry, LayoutPosition, Plugin, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import DoughnutLabel from "chartjs-plugin-doughnutlabel-v3";
import { Guid } from "guid-typescript";
import _ from "lodash";
import React, { ReactNode } from "react";
import { Chart } from "react-chartjs-2";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import DynamicStyleManager from "../../../shared/utilty/dynamic-style-manager";
import { KMainFunctions } from "../../../shared/utilty/main-functions";
import { CommonProps } from "../common-props";

declare let window: Window & { kuika: any };

export interface BaseChartProps {
  dataSource: any[];
  mode: keyof ChartTypeRegistry;
  leftAxis: boolean;
  rightAxis: boolean;
  beginAtZero: boolean;
  legend: LayoutPosition;
  legendAlign?: "start" | "center" | "end";
  legendFontSize?: number;
  dataLabelKeyName?: string;
  dataDisplayLabelKeyName?: string;
  dataTooltipFooterDataLabel?: string;
  firstDataFieldKeyName?: string;
  firstDataBgColorKeyName?: string;
  secondDataFieldKeyName?: string;
  secondDataBgColorKeyName?: string;
  thirdDataFieldKeyName?: string;
  thirdDataBgColorKeyName?: string;
  fill?: boolean;
  style?: React.CSSProperties;
  maxYAxisValue?: number;
  centerText?: string;
  children?: any;
  showDataLabels?: boolean;
  dataLabelFormatter?: string;
  dataLabelPosition?: "top" | "center" | "bottom";
  dataTargetLineKeyName?: string;
  multiTooltipContentField?: string;
  startAngle?: number;
  levelFrequency?: number;
  showLabelBoxes?: boolean;
  barThickness?: number;
  lineColor?: string;
  lineThickness?: number;
  lineDotWidth?: number;
  showLineLabel?: boolean;
  lineLabelColor?: string;
  lineLabelPosition?: "top" | "center" | "bottom";
}

interface BaseChartState {}

type ChartDataType = ChartData<
  "bar" | "line" | "doughnut" | "radar" | "pie" | "bubble" | "polarArea" | "scatter"
>;

class BaseChart extends React.PureComponent<BaseChartProps & CommonProps, BaseChartState> {
  private uniqueKey = Guid.create();

  private style = new DynamicStyleManager(`kbasechart_${this.uniqueKey}`);

  private generateSecureId(): string {
    const guid = Guid.create();
    return `bar-chart-${guid.toString()}`;
  }

  private canvasId: string = this.generateSecureId();

  private colorMap: { [key: string]: string } = {};

  private colorPalette: string[] = ["#86D497", "#FFDF6E", "#689FF2", "#FF6A6A", "#FFA462", "#AB96FF", "#F596DA"];

  private currentColorIndex: number = 0;

  private customLabelBoxesPlugin: Plugin = {
    id: "customLabelBoxes",
    beforeDraw: (chart: any) => {
      if (this.props.mode !== "radar" || !this.props.showLabelBoxes) return;

      const ctx = chart.ctx;
      const { width, height } = chart.canvas;
      const chartArea = chart.chartArea;

      const centerX = chartArea.left + (chartArea.right - chartArea.left) / 2;
      const centerY = chartArea.top + (chartArea.bottom - chartArea.top) / 2;

      const scale = chart.scales.r;
      const labels = chart.data.labels;

      labels.forEach((label: string, i: number) => {
        const startAngle = -Math.PI / 2;
        const angle = startAngle + (i * 2 * Math.PI) / labels.length;

        const datasource = this.props.dataSource ? this.props.dataSource : [];

        const dataPoint = datasource[i];
        if (!dataPoint) return;

        const firstField = dataPoint[KMainFunctions.toCamelCase(this.props.firstDataFieldKeyName)];
        const secondField = dataPoint[KMainFunctions.toCamelCase(this.props.secondDataFieldKeyName)];

        if (firstField === undefined) return;

        const baseRadius = scale.drawingArea;
        const padding = 40;
        const radius = baseRadius + padding;

        let x = centerX + Math.cos(angle) * radius;
        let y = centerY + Math.sin(angle) * radius;

        const boxWidth = 96;
        const boxHeight = 40;

        let fill = "#FFEFC6";

        if (Math.cos(angle) > 0.5) {
          x += boxWidth / 1;
          y += boxHeight * 1.5;
          if (Math.sin(angle) < -0.3) {
            x -= boxWidth / 8;
            y += boxHeight / 1.5;
          }
          if (Math.sin(angle) < 0 && Math.cos(angle) > 0.9) {
            x += boxWidth / 16;
            y -= boxHeight / 1;
          }
          if (Math.sin(angle) > -0.3 && Math.sin(angle) < 0.4) {
            x += boxWidth / 16;
            y -= boxHeight / 1.5;
          }
          if (Math.sin(angle) < -0.4) {
            x += boxWidth / 2;
            y -= boxHeight / 8;
          }
        } else if (Math.cos(angle) < -0.5) {
          x -= boxWidth / 16;
          y += boxHeight * 1.5;

          if (Math.sin(angle) < -0.2) {
            x -= boxWidth / 4;
            y -= boxHeight / 2;
          }
          if (Math.sin(angle) < -0.4) {
            x -= boxWidth / 4;
            y += boxHeight / 1;
          }

          if (Math.sin(angle) > 0 && Math.cos(angle) < -0.9) {
            x += boxWidth / 16;
            y -= boxHeight / 1.5;
          }
        }

        if (Math.sin(angle) > 0.5) {
          y += boxHeight / 2;
          x -= boxWidth / 16;

          if (Math.sin(angle) > 0.2 && Math.cos(angle) > 0.5) {
            x += boxWidth / 8;
            y -= boxHeight / 0.75;
          } else if (Math.sin(angle) > 0.2 && Math.cos(angle) < -0.5) {
            x += boxWidth / 12;
            y -= boxHeight / 0.75;
          } else if (Math.sin(angle) > 0 && Math.cos(angle) < 1) {
            x += boxWidth / 2;
            y += boxHeight / 4;
          }
        } else if (Math.sin(angle) < -0.5) {
          y -= boxHeight / 1;
        }

        ctx.save();

        ctx.fillStyle = fill;
        ctx.strokeStyle = "#E6E6E6";

        ctx.beginPath();
        ctx.roundRect(x - boxWidth / 2, y - boxHeight / 2, boxWidth, boxHeight, 5);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = "#000";
        ctx.font = "10px Arial";
        ctx.textAlign = "center";
        ctx.fillText(`${this.props.firstDataFieldKeyName}: ${this.getFormattedValue(firstField)}`, x, y - 8);
        ctx.fillText(
          `${this.props.secondDataFieldKeyName}: ${secondField ? this.getFormattedValue(secondField) : "-"}`,
          x,
          y + 12
        );

        ctx.restore();
      });
    }
  };

  constructor(props: BaseChartProps) {
    super(props);
    this.state = {};
    ChartJS.register(...registerables);
    ChartJS.register(ChartDataLabels);
    ChartJS.register(DoughnutLabel);
  }

  componentDidMount(): void {
    this.setDynamicStyle();
    this.updatePluginRegistration();
  }

  componentDidUpdate(prevProps: BaseChartProps): void {
    this.setDynamicStyle();

    if (prevProps.mode !== this.props.mode || prevProps.showLabelBoxes !== this.props.showLabelBoxes) {
      this.updatePluginRegistration();
    }
  }

  componentWillUnmount() {
    this.style.removeStyle();
  }

  private updatePluginRegistration(): void {
    ChartJS.unregister(this.customLabelBoxesPlugin);

    if (this.props.mode === "radar" && this.props.showLabelBoxes) {
      ChartJS.register(this.customLabelBoxesPlugin);
    }
  }

  getProps(): BaseChartProps {
    const props: BaseChartProps = { ...this.props };

    if (props.leftAxis) {
      delete props.leftAxis;
    }

    if (props.rightAxis) {
      delete props.leftAxis;
    }

    return props;
  }

  getNumbersOfStyleProperties = (val: any) => {
    let numb: any = val?.match(/\d/g);
    numb = numb?.join("");
    return numb;
  };

  getRandomColor(key: string): string {
    if (
      this.colorMap[key] &&
      this.props.mode !== "pie" &&
      this.props.mode !== "doughnut" &&
      this.props.mode !== "polarArea"
    ) {
      return this.colorMap[key];
    }

    const guid = Guid.create();
    const guidValue = guid.toString().replace(/-/g, "");
    const randomNumber = parseInt(guidValue.substring(0, 8), 16);
    const randomIndex = randomNumber % this.colorPalette.length;

    const color = this.colorPalette[randomIndex];
    this.colorMap[key] = color;
    return color;
  }

  getHeight(): string | undefined {
    if (this.props.style && this.props.style?.height) {
      return this.props.style.height.toString();
    }

    return undefined;
  }

  getMinHeight(): string | undefined {
    if (this.props.style && this.props.style?.minHeight) {
      return this.props.style.minHeight.toString();
    }
    return undefined;
  }

  getMaxHeight(): string | undefined {
    if (this.props.style && this.props.style?.maxHeight) {
      return this.props.style.maxHeight.toString();
    }

    return undefined;
  }

  getWidth(): string | undefined {
    if (this.props.style && this.props.style?.width) {
      return this.props.style.width.toString();
    }

    return undefined;
  }

  getMinWidth(): string | undefined {
    if (this.props.style && this.props.style?.minWidth) {
      return this.props.style.minWidth.toString();
    }

    return undefined;
  }

  getMaxWidth(): string | undefined {
    if (this.props.style && this.props.style?.maxWidth) {
      return this.props.style.maxWidth.toString();
    }

    return undefined;
  }

  getPaddingLeft(): string | undefined {
    if (this.props.style && this.props.style?.paddingLeft) {
      return `${this.props.style.paddingLeft.toString()}px`;
    }

    return undefined;
  }

  getPaddingTop(): string | undefined {
    if (this.props.style && this.props.style?.paddingTop) {
      return `${this.props.style.paddingTop.toString()}px`;
    }

    return undefined;
  }

  getPaddingRight(): string | undefined {
    if (this.props.style && this.props.style?.paddingRight) {
      return `${this.props.style.paddingRight.toString()}px`;
    }

    return undefined;
  }

  getPaddingBottom(): string | undefined {
    if (this.props.style && this.props.style?.paddingBottom) {
      return `${this.props.style.paddingBottom.toString()}px`;
    }

    return undefined;
  }

  getData(): ChartDataType {
    let labels: string[] = [];
    let displayLabels: string[] = [];
    let tooltipFooterLabels: string[] = [];
    const dataSets: any[] = [];
    const fill: boolean = this.props.fill ?? false;
    const barThickness = this.props.barThickness;

    if (window.kuika?.dashboardState === 1) {
      return {
        labels: ["Label 0", "Label 1", "Label 2", "Label 3", "Label 4", "Label 5"],
        datasets: [
          {
            label: "1",
            data: [24, 67, 49, 13, 92, 75],
            backgroundColor: this.colorPalette,
            fill
          }
        ]
      };
    }

    if (!this.props.dataSource || !Array.isArray(this.props.dataSource) || this.props.dataSource.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }

    if (this.props.dataDisplayLabelKeyName && this.props.dataDisplayLabelKeyName.length > 0) {
      labels =
        this.props.dataSource?.map((x) => x[KMainFunctions.toCamelCase(this.props.dataDisplayLabelKeyName)]) || [];
    } else {
      labels =
        this.props.dataSource?.map((x, idx) => {
          return `Label ${idx}`;
        }) || [];
    }

    if (this.props.firstDataFieldKeyName) {
      displayLabels =
        this.props.dataSource?.map((x, idx) => {
          return x[KMainFunctions.toCamelCase(this.props.firstDataFieldKeyName)];
        }) || [];
    } else if (this.props.dataLabelKeyName && this.props.dataLabelKeyName.length > 0) {
      displayLabels =
        this.props.dataSource?.map((x) => x[KMainFunctions.toCamelCase(this.props.dataLabelKeyName)]) || [];
    } else {
      displayLabels =
        this.props.dataSource?.map((x, idx) => {
          return `${idx}`;
        }) || [];
    }

    const tooltipTitleLabels =
      this.props.dataSource?.map((x) => x[KMainFunctions.toCamelCase(this.props.dataLabelKeyName)]) || [];

    if (this.props.dataTooltipFooterDataLabel && this.props.dataTooltipFooterDataLabel.length > 0) {
      tooltipFooterLabels =
        this.props.dataSource?.map((x) => x[KMainFunctions.toCamelCase(this.props.dataTooltipFooterDataLabel)]) || [];
    } else {
      tooltipFooterLabels =
        this.props.dataSource?.map((x, idx) => {
          return `${idx}`;
        }) || [];
    }

    const createDataset = (
      fieldKeyName: string,
      bgColorKeyName: string,
      datasetLabel: string,
      datasetIndex: number
    ) => {
      let bgColor: string[] = [];

      if (bgColorKeyName && bgColorKeyName.length > 0) {
        bgColor = this.props.dataSource?.map((x) => x[KMainFunctions.toCamelCase(bgColorKeyName)]);
      } else {
        bgColor = this.props.dataSource?.map((x) => this.getRandomColor(fieldKeyName));
      }

      const data: any[] = this.props.dataSource?.map((x) => x[KMainFunctions.toCamelCase(fieldKeyName)]);

      const datasetDisplayLabels = this.props.dataSource?.map((x) => x[KMainFunctions.toCamelCase(fieldKeyName)]) || [];

      const dataset: {
        label: string;
        data: any[];
        backgroundColor: string[];
        fill: boolean;
        displayLabels: string[];
        tooltipFooterLabels: string[];
        tooltipTitleLabels?: any[];
        borderColor?: string[];
        pointBackgroundColor?: string[];
        pointBorderColor?: string[];
        barThickness?: number;
        borderWidth?: number;
        pointRadius?: number;
        pointHoverRadius?: number;
        type?: string;
      } = {
        label: datasetLabel || `${datasetIndex}`,
        data,
        backgroundColor: bgColor,
        fill,
        displayLabels: datasetDisplayLabels,
        tooltipFooterLabels,
        tooltipTitleLabels
      };

      if (this.props.mode === "radar") {
        dataset.borderColor = bgColor;
        dataset.pointBackgroundColor = bgColor;
        dataset.pointBorderColor = bgColor;
      }

      if (this.props.mode === "bar" && barThickness) {
        dataset.barThickness = barThickness;
      }

      if (this.props.mode === "line") {
        if (this.props.lineColor) {
          dataset.borderColor = Array(data.length).fill(this.props.lineColor);
        }

        if (this.props.lineThickness !== undefined && this.props.lineThickness !== null) {
          const thickness = Math.min(Math.max(this.props.lineThickness, 1), 10) * 2;
          dataset.borderWidth = thickness;
        }

        if (this.props.lineDotWidth !== undefined && this.props.lineDotWidth !== null) {
          const dotWidth = Math.min(Math.max(this.props.lineDotWidth, 1), 10) * 2;
          dataset.pointRadius = dotWidth;
          dataset.pointHoverRadius = dotWidth + 2;
        }

        dataset.pointBackgroundColor = bgColor || dataset.borderColor;
        dataset.pointBorderColor = bgColor || dataset.borderColor;
      }

      return dataset;
    };

    if (this.props.firstDataFieldKeyName && this.props.firstDataFieldKeyName?.length > 0) {
      dataSets.push(
        createDataset(
          this.props.firstDataFieldKeyName,
          this.props.firstDataBgColorKeyName,
          this.props.firstDataFieldKeyName,
          1
        )
      );
    }

    if (this.props.secondDataFieldKeyName && this.props.secondDataFieldKeyName?.length > 0) {
      dataSets.push(
        createDataset(
          this.props.secondDataFieldKeyName,
          this.props.secondDataBgColorKeyName,
          this.props.secondDataFieldKeyName,
          2
        )
      );
    }

    if (this.props.thirdDataFieldKeyName && this.props.thirdDataFieldKeyName?.length > 0) {
      dataSets.push(
        createDataset(
          this.props.thirdDataFieldKeyName,
          this.props.thirdDataBgColorKeyName,
          this.props.thirdDataFieldKeyName,
          3
        )
      );
    }

    if (this.props.dataTargetLineKeyName && this.props.dataTargetLineKeyName?.length > 0) {
      const targetData: any[] = this.props.dataSource?.map(
        (x) => x[KMainFunctions.toCamelCase(this.props.dataTargetLineKeyName)]
      );

      const targetLineConfig = {
        label: this.props.dataTargetLineKeyName || "Target",
        data: targetData,
        type: "line",
        borderColor: this.props.lineColor || "#000000",
        borderWidth:
          this.props.lineThickness !== undefined && this.props.lineThickness !== null
            ? Math.min(Math.max(this.props.lineThickness, 1), 10) * 2
            : 2,
        fill: false,
        pointRadius:
          this.props.lineDotWidth !== undefined && this.props.lineDotWidth !== null
            ? Math.min(Math.max(this.props.lineDotWidth, 1), 10) * 2
            : 4,
        pointBackgroundColor: this.props.lineColor || "#000000",
        pointBorderColor: this.props.lineColor || "#000000",
        pointBorderWidth: 1,
        pointHoverRadius:
          this.props.lineDotWidth !== undefined && this.props.lineDotWidth !== null
            ? Math.min(Math.max(this.props.lineDotWidth, 1), 10) * 2 + 2
            : 6,
        pointHoverBackgroundColor: this.props.lineColor || "#000000",
        pointHoverBorderColor: this.props.lineColor || "#000000",
        pointHoverBorderWidth: 2,
        order: -1,
        isTargetLine: true,
        displayLabels: targetData
      };

      dataSets.push(targetLineConfig);
    }

    return {
      labels,
      datasets: dataSets
    };
  }

  getDynamicCss = (): string => {
    const className: string = this.canvasId;
    if (!className || className.length === 0) {
      return "";
    }
    let result = "";

    const { height, maxHeight, minHeight, width, minWidth, maxWidth } = this.props.style;

    if (height) {
      const isPercentage = typeof height === "string" && height.includes("%");
      if (isPercentage) {
        result += `.${className} {
          height: ${height} !important;
        }
        .${className} canvas {
          height: 100% !important;
        }`;
      } else if (!maxHeight && !minHeight) {
        result += `.${className} {
          height: unset !important;
          max-height: ${height} !important;
          min-height: ${height} !important;
        }`;
      }
    }

    if ((width && !maxWidth && !minWidth) || minWidth < width || maxWidth > width) {
      result += `.${className} {
        width: unset !important;
        max-width: ${width} !important;
        min-width: ${width} !important;
      }`;
    }

    return result;
  };

  setDynamicStyle = () => {
    this.style.setDynamicCss(this.getDynamicCss());
  };

  getFormattedValue(value) {
    let { dataLabelFormatter: formatter } = this.props;
    if (formatter && value && value?.toString()?.trim() !== "") {
      switch (formatter) {
        case "money":
          if (Number.isNaN(Number(value))) {
            return "0.00";
          }

          return new Intl.NumberFormat("tr-TR", {
            style: "currency",
            currency: "TRY",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })
            .format(Number(value))
            .replace("₺", "");

        case "money-var-1":
          if (Number.isNaN(Number(value))) {
            return "0.00";
          }

          return new Intl.NumberFormat("tr-TR", {
            style: "currency",
            currency: "TRY",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          })
            .format(Number(value))
            .replace("₺", "");

        case "money-tl":
          if (Number.isNaN(Number(value))) {
            return "₺0.00";
          }

          return new Intl.NumberFormat("tr-TR", {
            style: "currency",
            currency: "TRY",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(Number(value));

        case "percent":
          return `${value}%`;

        case "fractional-2":
          const number2 = Number(value);
          return isNaN(number2) ? value : number2.toFixed(2);

        case "fractional-5":
          const number5 = Number(value);
          return isNaN(number5) ? value : number5.toFixed(5);
        default:
          return value;
      }
    } else {
      if (value && typeof value === "string") {
        const kuikaCulture = localStorage.getItem("ml");
        const isLangTR = kuikaCulture?.toLowerCase() === "tr_tr";

        if (this.props.style?.textTransform === "lowercase" && (value.includes("İ") || value.includes("I"))) {
          if (isLangTR) {
            value = value.replace(/I/g, "ı");
          }
          value = value.replace(/İ/g, "i");
        } else if (this.props.style?.textTransform === "uppercase" && (value.includes("i") || value.includes("ı"))) {
          if (isLangTR) {
            value = value.replace(/i/g, "İ");
          }
          value = value.replace(/ı/g, "I");
        } else if (this.props?.style.textTransform === "capitalize") {
          if (isLangTR && value[0] === "i") {
            value = value.replace("i", "İ");
          } else if (value[0] === "ı") {
            value = value.replace("ı", "I");
          }
        }
      }

      return value?.toString();
    }
  }

  render(): ReactNode {
    const data = this.getData();
    let labels = [];

    if (this.props.children) {
      if (Array.isArray(this.props.children) && this.props.children.length > 1) {
        labels = this.props.children.map((child: any) => {
          return {
            text: child?.props?.text ?? "",
            font: {
              size: this.getNumbersOfStyleProperties(child?.props?.style?.fontSize) ?? "14",
              weight: child?.props?.style?.fontWeight ?? "medium",
              family: child?.props?.style?.fontFamily ?? "Arial"
            },
            color: child?.props?.style?.color ?? "black"
          };
        });
      } else if (this.props.children) {
        labels = [
          {
            text: this.props.children?.props?.text ?? "",
            font: {
              size: this.getNumbersOfStyleProperties(this.props.children?.props?.style?.fontSize) ?? "14",
              weight: this.props.children?.props?.style?.fontWeight ?? "medium",
              family: this.props.children?.props?.style?.fontFamily ?? "Arial"
            },
            color: this.props.children?.props?.style?.color ?? "black"
          }
        ];
      }
    }

    const maxYValue = Math.max(...data?.datasets?.map((x: any) => Math.max(...x?.data)));
    const options: any = {
      plugins: {
        legend: {
          position: this.props.legend,
          display:
            typeof this.props.legend === "string" &&
            this.props.legend &&
            ["top", "right", "bottom", "left"].indexOf(this.props.legend) > -1,
          align:
            (this.props.style?.textAlign as any) === "-webkit-right"
              ? "end"
              : (this.props.style?.textAlign as any) === "-webkit-left"
              ? "start"
              : "center",

          labels: {
            boxWidth: 15,
            boxHeight: 15,
            usePointStyle: true,
            pointStyle: "circle",
            font: {
              size: this.props.style?.fontSize ?? 12
            },
            ...(this.props.mode === "line" && {
              onClick: (e, legendItem, legend) => {
                const index = legendItem.index;
                const chart = legend.chart;
                const meta = chart.getDatasetMeta(index);

                meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : !meta.hidden;

                chart.update();
              },
              generateLabels: (chart) => {
                const datasets = chart.data.datasets;
                const { legendAlign } = this.props;
                return chart._getSortedDatasetMetas().map((meta) => {
                  const style = meta.controller.getStyle(meta.index);
                  return {
                    text: datasets[meta.index].label,
                    fillStyle: style.borderColor || style.backgroundColor,
                    strokeStyle: style.borderColor || style.backgroundColor,
                    lineWidth: 0,
                    hidden: !meta.visible,
                    index: meta.index,
                    align: legendAlign
                  };
                });
              }
            })
          }
        },
        ...(labels &&
          labels.length > 0 && {
            doughnutLabel: {
              labels
            }
          }),
        ChartDataLabels,
        tooltip: {
          usePointStyle: true,
          boxWidth: 8,
          boxHeight: 8,
          bodySpacing: 4,
          padding: 10,
          callbacks: window.kuika?.isDesignTime
            ? undefined
            : {
                title: this.props.dataLabelKeyName
                  ? (context: any) => {
                      if (context[0]?.dataset?.type !== "line" && context[0]?.dataset?.tooltipTitleLabels) {
                        return context[0].dataset.tooltipTitleLabels[context[0].dataIndex];
                      }
                      return context.label;
                    }
                  : undefined,
                label: this.props.multiTooltipContentField
                  ? (context: any) => {
                      if (this.props.multiTooltipContentField) {
                        const fields = this.props.multiTooltipContentField.split(",");
                        return fields.map((field) => {
                          const value = this.props.dataSource[context.dataIndex][field.trim()];
                          return `${field.trim()}: ${this.getFormattedValue(value)}`;
                        });
                      }
                    }
                  : this.props.dataTooltipFooterDataLabel
                  ? (context: any) => {
                      if (context?.dataset?.tooltipFooterLabels && context?.dataset?.type !== "line") {
                        return null;
                      }
                      if (context?.dataset?.type === "line") {
                        return `${context.dataset.label}:${context.formattedValue}`;
                      }
                      return context.label;
                    }
                  : undefined,
                footer: this.props.dataTooltipFooterDataLabel
                  ? (context: any) => {
                      if (context[0]?.dataset?.tooltipFooterLabels && context[0]?.dataset?.type !== "line") {
                        return context[0].dataset.tooltipFooterLabels[context[0].dataIndex];
                      }
                      return null;
                    }
                  : undefined,
                labelPointStyle: (context) => {
                  return {
                    pointStyle: "circle",
                    rotation: 0
                  };
                },
                labelColor: (context) => {
                  const dataset = context.dataset;
                  const colors = dataset.backgroundColor || dataset.borderColor;
                  const color = Array.isArray(colors) ? colors[context.dataIndex] : colors;

                  return {
                    borderColor: color,
                    backgroundColor: color,
                    borderWidth: 1,
                    borderRadius: 3
                  };
                }
              },
          displayColors: true,
          titleMarginBottom: 8,
          itemSort: (a, b) => {
            if (a.datasetIndex < b.datasetIndex) return -1;
            if (a.datasetIndex > b.datasetIndex) return 1;
            return 0;
          },
          titleFont: {
            weight: "bold",
            size: 12
          },
          bodyFont: {
            size: 11
          },
          footerFont: {
            weight: "normal",
            style: "italic",
            size: 11
          }
        },
        datalabels: {
          formatter: (value: any, context: any) => {
            const isTargetLine = context.dataset.isTargetLine;

            if (isTargetLine) {
              if (!this.props.showLineLabel) return null;
              if (window.kuika?.isDesignTime) return value;
              const index = context.dataIndex;
              return context.dataset.displayLabels && context.dataset.displayLabels[index]
                ? this.getFormattedValue(context.dataset.displayLabels[index])
                : null;
            }

            if (!this.props.showDataLabels) return null;
            if (window.kuika?.isDesignTime) return value;
            const index = context.dataIndex;
            return context.dataset.displayLabels && context.dataset.displayLabels[index]
              ? this.getFormattedValue(context.dataset.displayLabels[index])
              : null;
          },
          color: (context: any) => {
            const isTargetLine = context.dataset.isTargetLine;
            if (isTargetLine) {
              return this.props.lineLabelColor ?? this.props.lineColor ?? "black";
            }
            return this.props.style?.color ?? "black";
          },
          font: {
            size: this.props.style?.fontSize ?? 14,
            weight: this.props.style?.fontWeight ?? "bold",
            family: this.props.style?.fontFamily ?? "Arial"
          },
          align: (context: any) => {
            const isTargetLine = context.dataset.isTargetLine;
            if (isTargetLine && this.props.lineLabelPosition) {
              return this.props.lineLabelPosition === "top"
                ? "top"
                : this.props.lineLabelPosition === "bottom"
                ? "bottom"
                : "center";
            }
            if (this.props.mode === "line" && this.props.dataLabelPosition) {
              return this.props.dataLabelPosition === "top"
                ? "top"
                : this.props.dataLabelPosition === "bottom"
                ? "bottom"
                : "center";
            }
            return "center";
          },
          anchor: (context: any) => {
            const isTargetLine = context.dataset.isTargetLine;
            if (isTargetLine && this.props.lineLabelPosition) {
              return this.props.lineLabelPosition === "top"
                ? "end"
                : this.props.lineLabelPosition === "bottom"
                ? "start"
                : "center";
            }
            if (this.props.mode === "line" && this.props.dataLabelPosition) {
              return this.props.dataLabelPosition === "top"
                ? "end"
                : this.props.dataLabelPosition === "bottom"
                ? "start"
                : "center";
            }
            return "center";
          }
        }
      },
      scales: {
        y: {
          display: this.props.leftAxis,
          beginAtZero: this.props.beginAtZero,
          max: this.props.maxYAxisValue ?? maxYValue
        },
        x: {
          display: this.props.rightAxis,
          beginAtZero: this.props.beginAtZero
        },
        ...(this.props.mode === "radar" && {
          r: {
            ticks: {
              stepSize: this.props.levelFrequency ?? 1
            },
            beginAtZero: this.props.beginAtZero
          }
        })
      },
      responsive: true
    };

    return (
      <Chart
        id={this.canvasId}
        type={this.props.mode}
        {...this.getProps()}
        options={_.cloneDeep(options)}
        className={this.canvasId}
        data={data}
        style={{
          height: this.getHeight(),
          minHeight: this.getMinHeight(),
          maxHeight: this.getMaxHeight(),
          width: this.getWidth(),
          minWidth: this.getMinWidth(),
          maxWidth: this.getMaxWidth(),
          paddingLeft: this.getPaddingLeft(),
          paddingRight: this.getPaddingRight(),
          paddingTop: this.getPaddingTop(),
          paddingBottom: this.getPaddingBottom()
        }}
      />
    );
  }
}
const baseChart = withCommonEvents(BaseChart);
export { baseChart as BaseChart };
